import type { RefObject } from 'react';
import { defineFrontendModule } from '~/lib/module';
import { dangerToolBase, detailsToolBase, generalToolBase, tabsToolBase } from '~/modules/entities/channel-settings-tools';
import { openCreateOrganizationDialog } from '~/modules/organization/create-organization-form';
import { organizationsListQueryOptions } from '~/modules/organization/query';
import { getRouter } from '~/routes/-router-instance';
import { lazyNamed } from '~/utils/lazy-named';

const OrganizationGeneralCard = lazyNamed(() => import('~/modules/organization/settings-tools'), 'OrganizationGeneralCard');
const OrganizationDetailsCard = lazyNamed(() => import('~/modules/organization/settings-tools'), 'OrganizationDetailsCard');
const OrganizationTabsCard = lazyNamed(() => import('~/modules/organization/settings-tools'), 'OrganizationTabsCard');
const OrganizationDeleteCard = lazyNamed(() => import('~/modules/organization/settings-tools'), 'OrganizationDeleteCard');
const OrganizationsGrid = lazyNamed(() => import('~/modules/organization/organizations-grid'), 'OrganizationsGrid');

/** Opens the create dialog from the menu section's plus button, then navigates to the new organization. */
function createOrganizationAction(triggerRef: RefObject<HTMLButtonElement | null>) {
  openCreateOrganizationDialog(triggerRef, (organization) =>
    getRouter().navigate({
      to: '/$tenantId/$organizationSlug/organization/members',
      params: { tenantId: organization.tenantId, organizationSlug: organization.slug },
    }),
  );
}

defineFrontendModule({
  name: 'organizations',
  owner: 'cella',
  scope: ['frontend', 'backend'],
  description: 'UI for managing organizations, the highest ancestor in the entity hierarchy.',
  channel: {
    entityType: 'organization',
    menuSection: { createAction: createOrganizationAction, label: 'c:organization_other' },
    listQuery: (params) => organizationsListQueryOptions(params),
  },
  tools: [
    {
      ...generalToolBase,
      slot: 'organization.settings',
      render: (organization) => <OrganizationGeneralCard organization={organization} />,
    },
    {
      ...detailsToolBase,
      slot: 'organization.settings',
      render: (organization) => <OrganizationDetailsCard organization={organization} />,
    },
    {
      ...tabsToolBase,
      slot: 'organization.settings',
      render: (organization) => <OrganizationTabsCard organization={organization} />,
    },
    {
      ...dangerToolBase('organization', 'c:organization'),
      slot: 'organization.settings',
      render: (organization) => <OrganizationDeleteCard organization={organization} />,
    },
    // The organizations a user is a member of, on their profile page.
    {
      id: 'organizations',
      label: 'c:organization_other',
      slot: 'user.profile',
      // fork: inside an organization the profile lists that organization's projects alone (project-module.tsx)
      render: ({ user, organizationId, isSheet }) =>
        !organizationId && (
          <div className="container pt-4">
            <OrganizationsGrid fixedQuery={{ relatableUserId: user.id }} saveDataInSearch={!isSheet} focusView={!isSheet} />
          </div>
        ),
    },
  ],
});
